import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Star, ArrowUpRight } from "lucide-react";

import { adminApi } from "@/lib/api";
import {
  AdminInput,
  AdminSelect,
  ConfirmDialog,
  EmptyState,
  FieldLabel,
  Modal,
  StatusBadge,
} from "@/components/admin/AdminUI";
import ImageUploadField from "@/components/admin/ImageUploadField";

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [newProject, setNewProject] = useState({
    slug: "",
    title: "",
    short_label: "Residential Project",
    cover_image: "",
    status: "published",
    featured: true,
  });

  const load = () => adminApi.listProjects().then(setProjects);

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const slug = newProject.slug || newProject.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      await adminApi.createProject({ ...newProject, slug });
      toast.success("Project created");
      setOpenCreate(false);
      setNewProject({ slug: "", title: "", short_label: "Residential Project", cover_image: "", status: "published", featured: true });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create project");
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await adminApi.deleteProject(confirmDelete.slug);
      toast.success("Project deleted");
      setConfirmDelete(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleFeatured = async (p) => {
    try {
      const updated = await adminApi.updateProject(p.slug, { featured: !p.featured });
      setProjects((prev) => prev.map((x) => (x.slug === updated.slug ? updated : x)));
    } catch {
      toast.error("Failed to update");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="body-text">Residential portfolio management.</p>
        <button type="button" onClick={() => setOpenCreate(true)} className="btn-primary" data-testid="project-new-btn">
          <Plus className="h-4 w-4" /> New Project
        </button>
      </div>

      {projects.length === 0 ? (
        <EmptyState title="No projects yet" description="Create your first residential project." />
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {projects.map((p) => (
            <div key={p.id} className="admin-card rounded-sm overflow-hidden group">
              <div className="relative aspect-[16/10] bg-ink-graphite overflow-hidden">
                {p.cover_image ? (
                  <img src={p.cover_image} alt={p.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="h-full w-full grid place-items-center text-brand-gray text-xs uppercase tracking-widest">No cover</div>
                )}
                  <div className="absolute top-3 left-3 flex gap-2">
                    <StatusBadge status={p.status} />
                    {p.featured && <StatusBadge status="featured" />}
                  </div>
              </div>
              <div className="p-4 md:p-5">
                <p className="eyebrow-muted">{p.short_label}</p>
                <h3 className="text-xl font-semibold mt-2">{p.title}</h3>
                <p className="text-xs text-brand-gray mt-1">/{p.slug}</p>
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Link to={`/admin/projects/${p.slug}`} className="btn-outline text-[10px] px-3 py-2">
                      Edit
                    </Link>
                    <Link to={`/residential-design/${p.slug}`} target="_blank" rel="noreferrer" className="btn-ghost text-[10px]">
                      View <ArrowUpRight className="h-3 w-3" />
                    </Link>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => toggleFeatured(p)}
                      className={`h-8 w-8 grid place-items-center rounded-sm border ${p.featured ? "border-brand-yellow text-brand-yellow" : "border-white/10 text-brand-gray hover:text-brand-yellow"}`}
                      aria-label="Toggle featured"
                    >
                      <Star className="h-4 w-4" fill={p.featured ? "currentColor" : "none"} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(p)}
                      className="h-8 w-8 grid place-items-center rounded-sm border border-white/10 text-brand-gray hover:text-brand-red hover:border-brand-red"
                      aria-label="Delete project"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={openCreate} onClose={() => setOpenCreate(false)} title="New Project">
        <form onSubmit={create} className="space-y-4">
          <label>
            <FieldLabel>Title</FieldLabel>
            <AdminInput required value={newProject.title} onChange={(e) => setNewProject({ ...newProject, title: e.target.value })} />
          </label>
          <label>
            <FieldLabel>Slug (optional)</FieldLabel>
            <AdminInput value={newProject.slug} onChange={(e) => setNewProject({ ...newProject, slug: e.target.value })} placeholder="auto from title" />
          </label>
          <div>
            <FieldLabel>Cover image</FieldLabel>
            <ImageUploadField
              value={newProject.cover_image}
              onChange={(value) => setNewProject({ ...newProject, cover_image: value })}
              category="residential"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label>
              <FieldLabel>Status</FieldLabel>
              <AdminSelect value={newProject.status} onChange={(e) => setNewProject({ ...newProject, status: e.target.value })}>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </AdminSelect>
            </label>
            <label>
              <FieldLabel>Featured</FieldLabel>
              <AdminSelect value={String(newProject.featured)} onChange={(e) => setNewProject({ ...newProject, featured: e.target.value === "true" })}>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </AdminSelect>
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setOpenCreate(false)} className="btn-outline">Cancel</button>
            <button type="submit" className="btn-primary">Create</button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!confirmDelete}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={doDelete}
        title="Delete project"
        description={`Delete project '${confirmDelete?.title}'? This cannot be undone.`}
      />
    </div>
  );
}
